export type AirtablePromotionItemTermsAndConditions = [
  {
    url: string;
  },
];

export type AirtablePromotionItem = {
  headline: string;
  bannerHeadline: string;
  id: string;
  heroImage?: AirtableAttachmentDetail;
  heroImageWeb?: AirtableAttachmentDetail;
  description: string;
  barcodes: string[];
  type: string;
  startDate: string;
  endDate: string;
  hbStartDate: string;
  hbEndDate: string;
  tcStartDate: string;
  tcEndDate: string;
  termsAndConditions: AirtablePromotionItemTermsAndConditions;
  brands: string;
  url: string;
  recordType: 'PROMOTION';
  activationUrlName: string;
  iFrameLink: string;
  externalMicrosite: string;
  drawDate: string;
  winners: string;
  status: string;
  eCommerceOnly: boolean;
  membersOnly: boolean;
  inStoreEntry: boolean;
  hideOffer: boolean;
  game: AirtablePromotionItemGame;
  bonusItemInputSpec: AirtablePromotionItemExtraUserInputSpec; // deprecated in favour of extraUserInputSpec
  extraUserInputSpec: AirtablePromotionItemExtraUserInputSpec;
  adhocPromotionMessage: string;
  adhocButtonText: string;
  noEntryPromo: boolean;
  noReceiptRequired: boolean;
  offersStore: boolean;

  // Frontend only fields added for convenience. TODO: Organise this better
  mflId: string;
};

// Types duplicated from middleware

export interface AirtableAttachmentDetail {
  id?: string;
  height?: number;
  type: 'image' | 'video';
  url?: string;
  width?: number;
  filename?: string;
  size?: number;
  thumbnails?: {
    full?: {
      url: string;
      width: number;
      height: number;
    };
    large?: {
      url: string;
      width: number;
      height: number;
    };
    small?: {
      url: string;
      width: number;
      height: number;
    };
  };
}

export type AirtableFeedItem = {
  headline: string;
  tileHeadline: string;
  heroImage?: AirtableAttachmentDetail;
  heroImageWeb?: AirtableAttachmentDetail;
  articleImage?: AirtableAttachmentDetail;
  youtube: string | null;
  embeddedJs: AirtableFeedItemEmbeddedJs | null;
  firstCopy: string;
  id: string;
  secondCopy: string;
  productNames: string;
  productBarcodes: string[];
  segment: string;
  start: string;
  end: string;
  brands: string[];
  recordType: 'FEED';
  url: string;
  postStart: string;
  postEnd: string;
};

/**
 * Cold-start deep-link stub returned by linking.ts `parse.article` when
 * `feedStore.discoverList` is empty (DIS-1201). The article screen narrows
 * via `'__pending' in article` and re-derives the real AirtableFeedItem
 * once `discoverList` populates.
 */
export type AirtableFeedItemStub = {
  url: string;
  __pending: true;
};

export type AirtableFeedItemEmbeddedJs = {
  src: string;
  mobile: {
    src?: string;
    width: string;
    height: string;
  };
  web: {
    src?: string;
    width: string;
    height: string;
  };
};

export type AirtablePromotionItemGame = {
  id: string;
  isPracticeEnabled: boolean;
  practiceText: string;
  enterText: string;
  playText: string;
  noPlayText: string;
};

export type AirtablePromotionItemExtraUserInputSpecBase = {
  mandatory: boolean;
  title: string;
  description: string;
  type: 'text' | 'select';
  placeholder: '';
};

export type AirtablePromotionItemExtraUserInputSpec =
  | ExtraUserInputSpecText
  | ExtraUserInputSpecSelect;

export type ExtraUserInputSpecText = AirtablePromotionItemExtraUserInputSpecBase;
export type ExtraUserInputSpecSelect = AirtablePromotionItemExtraUserInputSpecBase & {
  items: {
    value: string;
    label: string;
  }[];
};

export type AirtableBrand = {
  brand: string;
  brandUrl: string;
  copy: string;
  headline: string;
  heroImage: AirtableAttachmentDetail;
  id: string;
  logo: AirtableAttachmentDetail[];
  promotions: string[];
};

export type AirtableInformation = {
  contentType: string;
  content: string;
};

export type AirtableHomepageItem = {
  content: string;
  brands: string[];
  brandsUrl: string[];
  heroImage?: AirtableAttachmentDetail;
  heroImageWeb?: AirtableAttachmentDetail;
  pageLink: string;
  platform: string[];
  type?: string;
  message?: string | null;
  isGlobal?: boolean;
};

export type HomepageCarouselSlide = {
  content: string;
  heroImage: AirtableAttachmentDetail;
  heroImageWeb: AirtableAttachmentDetail;
  pageLink: string;
  platform: string[];
  brands: string[];
  brandsUrl: string[];
};

export type HomepageCarousel = {
  slides: HomepageCarouselSlide[];
};

export const isCarouselItem = (item: AirtableHomepageItem): boolean => item.type === 'carousel';

export type HomepageResponse = AirtableHomepageItem[];
